import { Suspense, lazy } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { AuthLayout } from '@/layouts/AuthLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { PublicRoute } from '@/routes/PublicRoute';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { RequireOrganization } from '@/routes/RequireOrganization';
import { ROUTES } from '@/constants';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const AccountsPayablePage = lazy(() => import('@/pages/finance/AccountsPayablePage'));
const AccountsReceivablePage = lazy(() => import('@/pages/finance/AccountsReceivablePage'));
const CashFlowPage = lazy(() => import('@/pages/finance/CashFlowPage'));
const TransactionsPage = lazy(() => import('@/pages/finance/TransactionsPage'));
const BankAccountsPage = lazy(() => import('@/pages/finance/BankAccountsPage'));
const ClientsPage = lazy(() => import('@/pages/customers/ClientsPage'));
const ClientDetailPage = lazy(() => import('@/pages/customers/ClientDetailPage'));
const SuppliersPage = lazy(() => import('@/pages/suppliers/SuppliersPage'));
const SupplierDetailPage = lazy(() => import('@/pages/suppliers/SupplierDetailPage'));
const ProductsPage = lazy(() => import('@/pages/products/ProductsPage'));
const CategoriesPage = lazy(() => import('@/pages/categories/CategoriesPage'));
const ProductTypesPage = lazy(() => import('@/pages/types/ProductTypesPage'));
const CostCentersPage = lazy(() => import('@/pages/costCenters/CostCentersPage'));
const ContractsPage = lazy(() => import('@/pages/contracts/ContractsPage'));
const AdjustmentsPage = lazy(() => import('@/pages/contracts/AdjustmentsPage'));
const IndicesPage = lazy(() => import('@/pages/contracts/IndicesPage'));
const QuotesPage = lazy(() => import('@/pages/quotes/QuotesPage'));
const QuoteDetailPage = lazy(() => import('@/pages/quotes/QuoteDetailPage'));
const QuoteFormPage = lazy(() => import('@/pages/quotes/QuoteFormPage'));
const BillingPage = lazy(() => import('@/pages/fiscal/BillingPage'));
const InvoicesPage = lazy(() => import('@/pages/fiscal/InvoicesPage'));
const DocumentsPage = lazy(() => import('@/pages/documents/DocumentsPage'));
const PasswordsPage = lazy(() => import('@/pages/passwords/PasswordsPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'));

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner />
    </div>
  );
}

export function AppRouter() {
  const element = useRoutes([
    {
      element: <PublicRoute />,
      children: [
        {
          element: <AuthLayout />,
          children: [
            { path: ROUTES.LOGIN, element: <LoginPage /> },
            { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
            { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
          ],
        },
      ],
    },
    {
      element: <ProtectedRoute />,
      children: [
        {
          element: <DashboardLayout />,
          children: [
            { path: ROUTES.SETTINGS, element: <SettingsPage /> },
            {
              element: <RequireOrganization />,
              children: [
                { path: ROUTES.DASHBOARD, element: <DashboardPage /> },
                { path: ROUTES.ACCOUNTS_PAYABLE, element: <AccountsPayablePage /> },
                { path: ROUTES.ACCOUNTS_RECEIVABLE, element: <AccountsReceivablePage /> },
                { path: ROUTES.CASH_FLOW, element: <CashFlowPage /> },
                { path: ROUTES.TRANSACTIONS, element: <TransactionsPage /> },
                { path: ROUTES.BANK_ACCOUNTS, element: <BankAccountsPage /> },
                { path: ROUTES.CLIENTS, element: <ClientsPage /> },
                { path: '/cadastros/clientes/:id', element: <ClientDetailPage /> },
                { path: ROUTES.SUPPLIERS, element: <SuppliersPage /> },
                { path: '/cadastros/fornecedores/:id', element: <SupplierDetailPage /> },
                { path: ROUTES.PRODUCTS, element: <ProductsPage /> },
                { path: ROUTES.CATEGORIES, element: <CategoriesPage /> },
                { path: ROUTES.PRODUCT_TYPES, element: <ProductTypesPage /> },
                { path: ROUTES.COST_CENTERS, element: <CostCentersPage /> },
                { path: ROUTES.CONTRACTS, element: <ContractsPage /> },
                { path: ROUTES.CONTRACT_ADJUSTMENTS, element: <AdjustmentsPage /> },
                { path: ROUTES.CONTRACT_INDICES, element: <IndicesPage /> },
                { path: ROUTES.QUOTES, element: <QuotesPage /> },
                { path: ROUTES.QUOTE_NEW, element: <QuoteFormPage /> },
                { path: '/comercial/orcamentos/:id/editar', element: <QuoteFormPage /> },
                { path: '/comercial/orcamentos/:id', element: <QuoteDetailPage /> },
                { path: ROUTES.BILLING, element: <BillingPage /> },
                { path: ROUTES.INVOICES, element: <InvoicesPage /> },
                { path: ROUTES.DOCUMENTS, element: <DocumentsPage /> },
                { path: ROUTES.PASSWORDS, element: <PasswordsPage /> },
                { path: ROUTES.REPORTS, element: <ReportsPage /> },
              ],
            },
          ],
        },
      ],
    },
    { path: ROUTES.HOME, element: <Navigate to={ROUTES.DASHBOARD} replace /> },
    { path: ROUTES.NOT_FOUND, element: <NotFoundPage /> },
  ]);

  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}
